import {type ReactNode, useState} from 'react';
import {Clapperboard, ListFilter, ShieldAlert, SlidersHorizontal, Wand2} from 'lucide-react';
import {
  Badge,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {type ImageMatchTier, imageMatchTierLabels} from '@/api/jobs';
import {type RdOptionsScope, type ItemFilters} from '@/api/items';
import {FlagFilterFields} from '@/components/flags/FlagFilterMenu';
import {IMAGE_MATCH_TIER_TOOLTIPS} from '@/components/items/ItemBadges';
import {ItemFilterFields} from '@/components/items/ItemFiltersMenu';
import {RdSignalFilter} from '@/components/investigations/RdSignalFilter';
import {FilterChip} from '@/components/shared/FilterChip';
import {type FlagFilter} from '@/lib/flagFilter';
import {type IncludeMode, type SearchQuery} from '@/lib/searchQuery';
import {cn} from '@/lib/utils';
import {type ItemFilterState, type RdFilter} from '@/store/uiStore';

// Strongest first - the same ordering the API returns matches in, and the order the funnel summary
// reads them out.
export const TIER_ORDER = ['Exact', 'PageConfirmed', 'Partial', 'PageOnly'] as const;

function TermChip({label, tone}: {readonly label: string; readonly tone: 'include' | 'exclude'}) {
  return (
    <Badge variant={tone === 'exclude' ? 'destructive' : 'secondary'}>
      {tone === 'exclude' ? '−' : ''}
      {label}
    </Badge>
  );
}

function CheckRow({
  checked,
  onChange,
  children
}: {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly children: ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 px-1.5 py-1 text-xs">
      <Checkbox
        checked={checked}
        onCheckedChange={(next) => {
          onChange(next);
        }}
      />
      {children}
    </label>
  );
}

/**
 Filters image-search results by how strongly each one matched the searched image. Client-side and
 over loaded items only - the tiers come from the job's scan record, not from the items query. A
 pressed tier is shown; an unpressed one is excluded.
 */
export function MatchStrengthFields({
  excluded,
  onExcludedChange,
  counts,
  hiddenCount,
  totalCount
}: {
  readonly excluded: ReadonlySet<ImageMatchTier>;
  readonly onExcludedChange: (next: ReadonlySet<ImageMatchTier>) => void;
  readonly counts: ReadonlyMap<ImageMatchTier, number>;
  readonly hiddenCount: number;
  readonly totalCount: number;
}) {
  return (
    <div className="space-y-1 px-1.5">
      <div className="text-muted-foreground text-xs">Match strength</div>
      <ToggleGroup
        multiple
        variant="outline"
        spacing={0}
        size="sm"
        className="flex-wrap"
        aria-label="Match strength"
        value={TIER_ORDER.filter((tier) => !excluded.has(tier))}
        onValueChange={(next) => {
          const shown = new Set(next as ImageMatchTier[]);
          onExcludedChange(new Set(TIER_ORDER.filter((tier) => !shown.has(tier))));
        }}>
        {TIER_ORDER.map((tier) => (
          <ToggleGroupItem key={tier} value={tier} title={IMAGE_MATCH_TIER_TOOLTIPS[tier]}>
            {imageMatchTierLabels[tier]}
            <span className="text-muted-foreground tabular-nums">{counts.get(tier) ?? 0}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {hiddenCount > 0 ? (
        <div className="text-muted-foreground text-xs">
          {hiddenCount} of {totalCount} hidden from loaded results
        </div>
      ) : null}
    </div>
  );
}

// Enforces the exclude (and optionally include) keyword operators the analyst typed into the job's
// search term client-side, since the YouTube Data API only treats `-term` as a negative ranking
// signal rather than a hard NOT.
//
// Exclusions are presented as the always-on part and includes as an opt-in, because the two are not
// equally safe: YouTube never honoured the exclusions, but it did already match the include terms
// - often semantically, against a transcript and signals we never see - so re-requiring them to
// appear literally in the fields we store can only discard items it thought relevant.
export function SmartFilterFields({
  query,
  enabled,
  onEnabledChange,
  includeMode,
  onIncludeModeChange,
  includesEnabled,
  onIncludesEnabledChange,
  stemming,
  onStemmingChange,
  hiddenCount,
  totalCount,
  hiddenChannelCount
}: {
  readonly query: SearchQuery;
  readonly enabled: boolean;
  readonly onEnabledChange: (next: boolean) => void;
  readonly includeMode: IncludeMode;
  readonly onIncludeModeChange: (next: IncludeMode) => void;
  readonly includesEnabled: boolean;
  readonly onIncludesEnabledChange: (next: boolean) => void;
  readonly stemming: boolean;
  readonly onStemmingChange: (next: boolean) => void;
  readonly hiddenCount: number;
  readonly totalCount: number;
  readonly hiddenChannelCount: number;
}) {
  const hasTerms = query.include.length > 0 || query.exclude.length > 0;

  return (
    <div className="space-y-1">
      <CheckRow checked={enabled} onChange={onEnabledChange}>
        <Wand2 className="text-muted-foreground size-3.5" />
        Smart filtering
      </CheckRow>
      {hasTerms ? (
        <>
          <div className="px-1.5 py-1">
            <div className="text-muted-foreground mb-1 text-xs">
              Exclude <span className="font-normal">(always applied)</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {query.exclude.length > 0 ? (
                query.exclude.map((term) => <TermChip key={term} label={term} tone="exclude" />)
              ) : (
                <span className="text-muted-foreground text-xs">None</span>
              )}
            </div>
          </div>
          {query.include.length > 0 ? (
            <>
              <CheckRow checked={includesEnabled} onChange={onIncludesEnabledChange}>
                Also require include terms
              </CheckRow>
              <div
                className={cn(
                  'flex items-center justify-between gap-3 px-1.5 py-1 transition-opacity',
                  !includesEnabled && 'pointer-events-none opacity-40'
                )}>
                <ToggleGroup
                  variant="outline"
                  spacing={0}
                  size="sm"
                  aria-label="Include mode"
                  disabled={!includesEnabled}
                  value={[includeMode]}
                  onValueChange={(next) => {
                    const option = next[0];
                    if (option === 'all' || option === 'any') {
                      onIncludeModeChange(option);
                    }
                  }}>
                  <ToggleGroupItem value="all" className="h-6">
                    AND
                  </ToggleGroupItem>
                  <ToggleGroupItem value="any" className="h-6">
                    OR
                  </ToggleGroupItem>
                </ToggleGroup>
                <div className="flex flex-1 flex-wrap justify-end gap-1">
                  {query.include.map((term) => (
                    <TermChip key={term} label={term} tone="include" />
                  ))}
                </div>
              </div>
            </>
          ) : null}
          <CheckRow checked={stemming} onChange={onStemmingChange}>
            Match word variants (plurals, tenses)
          </CheckRow>
          <div className="text-muted-foreground px-1.5 py-1 text-xs">
            {hiddenCount} of {totalCount} hidden from loaded results{' '}
            {includesEnabled
              ? '(contain excluded terms, or are missing the search terms)'
              : '(contain excluded terms)'}
            {hiddenChannelCount > 0 ? (
              <>
                <br />+{hiddenChannelCount} {hiddenChannelCount === 1 ? 'channel' : 'channels'}{' '}
                fully hidden
              </>
            ) : null}
          </div>
        </>
      ) : (
        <div className="text-muted-foreground px-1.5 py-1.5 text-xs">
          This search has no keyword operators to apply.
        </div>
      )}
    </div>
  );
}

type SmartFilterFieldsProps = Parameters<typeof SmartFilterFields>[0];
type MatchStrengthFieldsProps = Parameters<typeof MatchStrengthFields>[0];

export type FilterCategory = 'risk' | 'terms' | 'content' | 'video';

export type InvestigationFiltersProps = {
  readonly activeCount: number;
  readonly categoryCounts: Readonly<Record<FilterCategory, number>>;
  readonly smartFilter?: SmartFilterFieldsProps;
  readonly matchStrength?: MatchStrengthFieldsProps;
  readonly flags: readonly FlagFilter[];
  readonly onFlagsChange: (next: FlagFilter[]) => void;
  readonly itemFilters: ItemFilterState;
  readonly onItemFiltersChange: (patch: Partial<ItemFilterState>) => void;
  readonly showPublished: boolean;
  readonly showLastPostSince: boolean;
  readonly rdSignals?: {
    readonly scope: RdOptionsScope;
    readonly filters: ItemFilters;
    readonly selections: RdFilter[];
  };
};

const CATEGORY_ICONS: Record<FilterCategory, ReactNode> = {
  risk: <ShieldAlert />,
  terms: <Wand2 />,
  content: <SlidersHorizontal />,
  video: <Clapperboard />
};

export function InvestigationFilters({
  activeCount,
  categoryCounts,
  smartFilter,
  matchStrength,
  flags,
  onFlagsChange,
  itemFilters,
  onItemFiltersChange,
  showPublished,
  showLastPostSince,
  rdSignals
}: InvestigationFiltersProps) {
  const categories: Array<{readonly key: FilterCategory; readonly label: string}> = [
    {key: 'video', label: 'Video & channel'},
    ...(smartFilter ? [{key: 'terms' as const, label: 'Search terms'}] : []),
    ...(rdSignals ? [{key: 'risk' as const, label: 'Risk signals'}] : []),
    {key: 'content', label: 'Flags'}
  ];
  const [chosen, setChosen] = useState<FilterCategory | null>(null);
  const current =
    categories.find((category) => category.key === chosen) ??
    categories.find((category) => categoryCounts[category.key] > 0) ??
    categories[0];

  return (
    <Popover>
      <PopoverTrigger
        render={
          <FilterChip
            active={activeCount > 0}
            icon={<ListFilter />}
            label="Filters"
            count={activeCount}
          />
        }
      />
      <PopoverContent align="start" className="w-[34rem] p-0">
        <div className="flex max-h-[70vh]">
          <div
            role="tablist"
            aria-orientation="vertical"
            aria-label="Filter categories"
            className="border-border flex w-40 shrink-0 flex-col gap-0.5 border-r p-2">
            {categories.map((category) => (
              <button
                key={category.key}
                type="button"
                role="tab"
                aria-selected={category.key === current?.key}
                aria-controls="investigation-filter-panel"
                className={cn(
                  'text-muted-foreground hover:bg-muted flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium [&_svg]:size-3.5',
                  category.key === current?.key && 'bg-muted text-foreground'
                )}
                onClick={() => {
                  setChosen(category.key);
                }}>
                {CATEGORY_ICONS[category.key]}
                <span className="flex-1">{category.label}</span>
                {categoryCounts[category.key] > 0 ? (
                  <Badge variant="secondary">{categoryCounts[category.key]}</Badge>
                ) : null}
              </button>
            ))}
          </div>
          <div
            id="investigation-filter-panel"
            role="tabpanel"
            aria-label={current?.label}
            className="min-w-0 flex-1 space-y-3 overflow-y-auto p-3">
            {current?.key === 'risk' && rdSignals ? (
              <RdSignalFilter {...rdSignals} onChange={onItemFiltersChange} />
            ) : null}
            {current?.key === 'terms' && smartFilter ? (
              <SmartFilterFields {...smartFilter} />
            ) : null}
            {current?.key === 'content' ? (
              <>
                {matchStrength ? <MatchStrengthFields {...matchStrength} /> : null}
                <FlagFilterFields value={flags} onChange={onFlagsChange} />
              </>
            ) : null}
            {current?.key === 'video' ? (
              <ItemFilterFields
                showLanguage
                filters={itemFilters}
                showPublished={showPublished}
                showLastPostSince={showLastPostSince}
                onChange={onItemFiltersChange}
              />
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
